// Agent audio devices (Audio settings in the cti2 Topbar menu): which mic
// the WebRTC softphone sends and which speaker every call/alert sound plays
// on. Saved per browser in localStorage — deviceIds are per-origin and
// per-machine, so there's nothing useful to keep server-side. '' = system
// default. Deliberately import-free so audioDevices.check.ts can run this
// file straight under node.

const MIC_KEY = 'cti.audio.micId';
const SPEAKER_KEY = 'cti.audio.speakerId';

const read = (key: string): string => {
  try { return localStorage.getItem(key) || ''; } catch { return ''; }
};

const write = (key: string, value: string) => {
  try {
    if (value) localStorage.setItem(key, value);
    else localStorage.removeItem(key);
  } catch { /* storage blocked — choice applies now but isn't remembered */ }
};

export const getSavedMicId = (): string => read(MIC_KEY);
export const getSavedSpeakerId = (): string => read(SPEAKER_KEY);
export const getSavedMicLabel = (): string => read(`${MIC_KEY}.label`);
export const getSavedSpeakerLabel = (): string => read(`${SPEAKER_KEY}.label`);

// Chrome adds 'default' / 'communications' pseudo-entries (and blank ids
// before mic permission); our '' option already means "system default".
export const listDevices = <T extends { deviceId: string; kind: string; label: string }>(
  devices: T[],
  kind: MediaDeviceKind,
): T[] =>
  devices.filter(
    (d) => d.kind === kind && d.deviceId && d.deviceId !== 'default' && d.deviceId !== 'communications',
  );

// A saved id the browser no longer lists (headset unplugged) resolves to
// default so the picker never shows a ghost device.
export const pickDeviceId = (savedId: string, devices: Array<{ deviceId: string }>): string =>
  savedId && devices.some((d) => d.deviceId === savedId) ? savedId : '';

export const withSavedDevice = <T extends { deviceId: string; label: string }>(
  devices: T[],
  savedId: string,
  savedLabel: string,
): Array<T | { deviceId: string; label: string }> =>
  savedId && !devices.some((d) => d.deviceId === savedId)
    ? [...devices, { deviceId: savedId, label: savedLabel }]
    : devices;

// `ideal`, not `exact`: an unplugged saved mic falls back to the default
// instead of failing the call with OverconstrainedError.
export const micConstraint = (micId: string = getSavedMicId()): boolean | MediaTrackConstraints =>
  micId ? { deviceId: { ideal: micId } } : true;

export const supportsSpeakerSelect = (): boolean =>
  typeof HTMLMediaElement !== 'undefined' && 'setSinkId' in HTMLMediaElement.prototype;

// Media elements and AudioContexts (Web Audio tones) both expose setSinkId.
type SoundSink = HTMLMediaElement | AudioContext;

export const applySpeaker = async (el: SoundSink, speakerId: string = getSavedSpeakerId()) => {
  if (typeof (el as any).setSinkId !== 'function') return;
  try {
    await (el as any).setSinkId(speakerId);
  } catch (e) {
    // Saved speaker gone → back to default rather than silence.
    if (speakerId) { try { await (el as any).setSinkId(''); } catch { /* keep current */ } }
    console.warn('setSinkId failed', e);
  }
};

// Detached Audio() objects and AudioContexts (ringback, DTMF) that must
// follow the speaker.
const trackedSounds = new Set<SoundSink>();

export const trackSound = <T extends SoundSink>(el: T): T => {
  trackedSounds.add(el);
  void applySpeaker(el);
  return el;
};

// Every <audio> on the page (remoteAudio, ringtone, hangupAudio) plus the
// tracked detached sounds.
export const applySpeakerToAll = (speakerId: string = getSavedSpeakerId()) => {
  document.querySelectorAll('audio').forEach((el) => { void applySpeaker(el, speakerId); });
  trackedSounds.forEach((el) => {
    if ((el as AudioContext).state === 'closed') trackedSounds.delete(el);
    else void applySpeaker(el, speakerId);
  });
};

// Re-apply on headset plug/unplug and when mic permission is granted —
// Chrome rejects a non-default sink until then, so the mount-time apply
// can silently fall back to default. Returns cleanup.
export const followSpeakerChanges = (): (() => void) => {
  const reapply = () => applySpeakerToAll();
  const md = navigator.mediaDevices;
  md?.addEventListener('devicechange', reapply);
  let perm: PermissionStatus | null = null;
  let stopped = false;
  navigator.permissions?.query({ name: 'microphone' as PermissionName })
    .then((p) => { if (stopped) return; perm = p; p.addEventListener('change', reapply); })
    .catch(() => { /* Firefox/Safari may not support the microphone query */ });
  return () => {
    stopped = true;
    md?.removeEventListener('devicechange', reapply);
    perm?.removeEventListener('change', reapply);
  };
};

export const saveAudioDevices = ({ micId, speakerId, micLabel = '', speakerLabel = '' }: {
  micId: string; speakerId: string; micLabel?: string; speakerLabel?: string;
}) => {
  write(MIC_KEY, micId);
  write(SPEAKER_KEY, speakerId);
  write(`${MIC_KEY}.label`, micId ? micLabel : '');
  write(`${SPEAKER_KEY}.label`, speakerId ? speakerLabel : '');
  applySpeakerToAll(speakerId);
};

// Swap the mic on a live call. SIP.js's setLocalMediaStream replaceTrack()s
// the audio sender (no re-INVITE) and stops the old track. Mute state is
// copied from the old track because AgentPortalPage's mute effect only
// re-runs on session/muteOn changes.
export const switchLiveMic = async (session: any, micId: string): Promise<boolean> => {
  const sdh = session?.sessionDescriptionHandler;
  const pc: RTCPeerConnection | undefined = sdh?.peerConnection;
  if (!pc || typeof sdh.setLocalMediaStream !== 'function') return false;
  const oldTrack = pc.getSenders().find((s) => s.track?.kind === 'audio')?.track;
  const stream = await navigator.mediaDevices.getUserMedia({ audio: micConstraint(micId) });
  stream.getAudioTracks().forEach((t) => { t.enabled = oldTrack ? oldTrack.enabled : true; });
  await sdh.setLocalMediaStream(stream);
  return true;
};
